import { CartUpdateEvent } from '@theme/events';

/**
 * Builds the section ids that should be refreshed after a cart mutation,
 * matching the pattern used by the theme's own product-form component.
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.add(item.dataset.sectionId);
    }
  });
  return Array.from(ids);
}

/**
 * Adds a line to the cart via the Ajax Cart API.
 *
 * IMPORTANT: this store's checkout validation only accepts
 * `application/x-www-form-urlencoded` bodies for /cart/add.js when a
 * selling_plan is involved - a JSON body gets rejected with "Variant can
 * only be purchased with a selling plan." even though the same fields are
 * present. Always add lines this way rather than with a JSON body.
 *
 * @param {Object} config
 * @param {string|number} config.id - variant id
 * @param {number} config.quantity
 * @param {string|number} [config.sellingPlanId]
 * @returns {Promise<Object>} the parsed JSON response
 */
function addToCart({ id, quantity, sellingPlanId }) {
  const params = new URLSearchParams();
  params.append('id', String(id));
  params.append('quantity', String(quantity));
  if (sellingPlanId) params.append('selling_plan', String(sellingPlanId));
  const sectionIds = getCartSectionIds();
  if (sectionIds.length) params.append('sections', sectionIds.join(','));

  return fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: params.toString(),
  }).then((response) => response.json());
}

/**
 * Removes/changes a line's quantity via the Ajax Cart API.
 * @param {Object} config
 * @param {string} config.id - cart line item key
 * @param {number} config.quantity
 * @returns {Promise<Object>} the parsed JSON response
 */
function changeCartLine({ id, quantity }) {
  const sectionIds = getCartSectionIds();
  return fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ id, quantity, sections: sectionIds.join(',') }),
  }).then((response) => response.json());
}

/**
 * Fetches freshly-rendered section HTML straight from the server, without
 * mutating the cart. Used as the single source of truth for what the drawer
 * should show after a plan switch, whatever happened during that switch -
 * this is what keeps the drawer from ever diverging from the real cart that
 * checkout reads from.
 * @param {string[]} sectionIds
 * @returns {Promise<Object>} map of section id -> rendered HTML
 */
function fetchSections(sectionIds) {
  if (!sectionIds.length) return Promise.resolve({});
  const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`;
  return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((response) => response.json());
}

/**
 * A custom element that lets a shopper switch the shave-plan (or upgrade a
 * one-time purchase into a subscription) directly from the cart drawer.
 *
 * Expects child buttons/links carrying:
 *   data-variant-id       - the target product variant to add
 *   data-selling-plan-id  - the selling plan to attach
 *   data-current          - "true" on whichever card is already in the cart
 *
 * The component itself carries:
 *   data-current-line-key - cart.js line item key of the line being replaced (may be empty)
 *   data-current-qty       - quantity to preserve across the swap
 *   data-section-id        - the shopify section id to re-render after the swap
 *
 * Swap order and error handling (fixed):
 * This used to remove the old line first and add the new one second, and
 * only refreshed the drawer (and only ever showed anything) on the success
 * path - if the add failed, the old line was already gone, the failure was
 * only ever logged to the console, and the drawer just kept showing
 * whatever it last rendered. That let the drawer visibly disagree with the
 * real cart (and therefore with checkout) whenever the add step failed.
 *
 * Now: the ADD happens first, so a failure there leaves the original line
 * completely untouched. The old line is only removed after the new one is
 * confirmed in the cart. And regardless of which step fails, the `finally`
 * block always re-fetches the real section HTML from the server and
 * re-dispatches it - so the drawer is guaranteed to reflect the actual cart
 * every time, never a stale optimistic guess.
 */
class FlynnysPlanSwitcher extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!button || this.hasAttribute('data-busy')) return;
    if (button.dataset.current === 'true') return;

    const variantId = button.dataset.variantId;
    const sellingPlanId = button.dataset.sellingPlanId;
    const currentKey = this.dataset.currentLineKey;
    const quantity = parseInt(this.dataset.currentQty || '1', 10) || 1;
    const sectionId = this.dataset.sectionId || '';

    if (!variantId) return;

    this.setAttribute('data-busy', '');
    this.#setLoading(true);
    this.#setError(null);

    let addSucceeded = false;

    // Captured rather than applied immediately - the finally block below
    // ALWAYS re-renders this section's markup fresh from the server (to
    // guarantee the drawer can never diverge from the real cart), and that
    // fresh, error-free HTML silently overwrites an error message set
    // before it lands. Apply it AFTER the resync instead, so the shopper
    // actually gets to see it rather than it flashing and vanishing.
    let pendingError = null;

    addToCart({ id: variantId, quantity, sellingPlanId })
      .then((addResponse) => {
        if (addResponse && addResponse.status) {
          throw new Error(`Flynnys plan switch: failed to add new plan - ${addResponse.description || addResponse.message}`);
        }
        addSucceeded = true;
        if (!currentKey) return null;
        return changeCartLine({ id: currentKey, quantity: 0 });
      })
      .then((removeResponse) => {
        if (removeResponse && removeResponse.status) {
          // The new plan is safely in the cart; only the old line's removal
          // failed. Don't hide this behind a silently-stale drawer - the
          // finally block below will resync to whatever the cart actually
          // has (both lines, in this case), and we tell the shopper why.
          console.error(
            `Flynnys plan switch: added the new plan but failed to remove the previous line - ${removeResponse.description || removeResponse.message}`
          );
          pendingError = "Added your new plan, but couldn't remove the previous item automatically - please check your cart.";
        }
      })
      .catch((error) => {
        console.error(error);
        if (!addSucceeded) {
          pendingError = "Sorry, we couldn't switch your plan just then. Please try again.";
        }
      })
      .finally(() => {
        // Always resync the drawer to the real, server-side cart state,
        // whatever happened above - this is what guarantees the drawer can
        // never diverge from what checkout will actually charge. This
        // re-renders the section's markup from the server, so any error
        // message must be (re-)applied AFTER it lands, not before.
        fetchSections(getCartSectionIds())
          .then((sections) => {
            document.dispatchEvent(
              new CartUpdateEvent({}, sectionId, { source: 'flynnys-plan-switcher', sections })
            );
          })
          .catch((error) => console.error('Flynnys plan switch: failed to resync cart sections', error))
          .finally(() => {
            // Double rAF: gives the section re-render triggered by the
            // CartUpdateEvent above a chance to finish patching the DOM
            // before we go looking for the (possibly brand new) error slot.
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (pendingError) {
                  // Re-find the error slot rather than assuming `this` is
                  // still attached - the resync may have replaced this
                  // element's own subtree, or even this element itself, with
                  // freshly-rendered markup carrying the same data-section-id.
                  const scope = this.isConnected
                    ? this
                    : document.querySelector(`flynnys-plan-switcher[data-section-id="${sectionId}"]`);
                  const el = scope ? scope.querySelector('.fc-planbox__error') : null;
                  if (el) {
                    el.textContent = pendingError;
                    el.hidden = false;
                  }
                }
                this.removeAttribute('data-busy');
                this.#setLoading(false);
              });
            });
          });
      });
  };

  /** @param {boolean} isLoading */
  #setLoading(isLoading) {
    this.querySelectorAll('[data-variant-id]').forEach((el) => {
      if (el instanceof HTMLButtonElement) el.disabled = isLoading;
      el.classList.toggle('fc-plan-btn--loading', isLoading);
    });
  }

  /** @param {string|null} message */
  #setError(message) {
    const el = this.querySelector('.fc-planbox__error');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.hidden = true;
      el.textContent = '';
    }
  }
}

if (!customElements.get('flynnys-plan-switcher')) {
  customElements.define('flynnys-plan-switcher', FlynnysPlanSwitcher);
}

/**
 * A tiny presentational carousel used for the cart drawer's
 * "You may also like" upsell rail. All slides are rendered up front by
 * Liquid; this just toggles which one is visible.
 */
class FlynnysUpsellCarousel extends HTMLElement {
  #index = 0;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-upsell-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-upsell-dot]'));
    this.prevButton = this.querySelector('[data-upsell-prev]');
    this.nextButton = this.querySelector('[data-upsell-next]');

    this.prevButton?.addEventListener('click', () => this.#go(-1));
    this.nextButton?.addEventListener('click', () => this.#go(1));
    this.addEventListener('click', this.#onAddClick);

    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onAddClick);
  }

  #onAddClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null;
    if (!button || button.hasAttribute('data-busy')) return;

    const variantId = button.getAttribute('data-upsell-add');
    if (!variantId) return;

    button.setAttribute('data-busy', '');

    addToCart({ id: variantId, quantity: 1 })
      .then((response) => {
        if (response && response.status) {
          throw new Error(`Flynnys upsell add error: ${response.description || response.message}`);
        }
        document.dispatchEvent(
          new CartUpdateEvent({}, this.dataset.sectionId || '', {
            source: 'flynnys-upsell-carousel',
            sections: response.sections,
          })
        );
      })
      .catch((error) => console.error(error))
      .finally(() => button.removeAttribute('data-busy'));
  };

  /** @param {number} delta */
  #go(delta) {
    if (!this.slides?.length) return;
    this.#index = (this.#index + delta + this.slides.length) % this.slides.length;
    this.#render();
  }

  #render() {
    this.slides?.forEach((slide, i) => {
      slide.hidden = i !== this.#index;
    });
    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.#index);
    });
  }
}

if (!customElements.get('flynnys-upsell-carousel')) {
  customElements.define('flynnys-upsell-carousel', FlynnysUpsellCarousel);
}
